// Where the visitor is, according to Cloudflare's public trace endpoint.
//
// This is a *hint*, and the backend treats it as one: it can localize what we
// show, but it can never unlock a cheaper market price, because anyone can put
// any country in a request. The trustworthy path is CF-IPCountry, which the
// edge adds server-side once traffic is proxied — this exists to cover the
// domains that aren't yet.

const TRACE_URL = 'https://1.1.1.1/cdn-cgi/trace';
const STORAGE_KEY = 'itsai_client_country';
const TIMEOUT_MS = 2000;

let inFlight: Promise<string | null> | null = null;

const readCached = (): string | null => {
    try {
        return sessionStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
};

const cache = (code: string) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, code);
    } catch {
        // Private mode / blocked storage: re-fetching next reload is fine.
    }
};

const parseCountry = (body: string): string | null => {
    const match = /^loc=([A-Z]{2})$/m.exec(body.trim());
    return match ? match[1] : null;
};

// Synchronous read for request headers — null until the lookup has resolved,
// which simply means the backend falls back to its default currency.
export const getClientCountry = (): string | null => readCached();

// Ad blockers and offline clients make this fail routinely; a failure just
// means no hint, so it is never surfaced as an error.
export const detectClientCountry = async (): Promise<string | null> => {
    const cached = readCached();
    if (cached) return cached;
    if (inFlight) return inFlight;

    inFlight = (async () => {
        try {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
            const response = await fetch(TRACE_URL, {signal: controller.signal});
            clearTimeout(timer);
            if (!response.ok) return null;

            const code = parseCountry(await response.text());
            if (code) cache(code);
            return code;
        } catch {
            return null;
        } finally {
            inFlight = null;
        }
    })();

    return inFlight;
};
